import axios from '../utils/axiosConfig'; // global axios interceptors (401/403)
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../Style/Register.css';

const MAX_PW_LEN = 128;

// EMAIL validation regex (RFC 5322-ish)
const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// helpers
const hasLower   = (s) => /[a-z]/.test(s);
const hasUpper   = (s) => /[A-Z]/.test(s);
const hasDigit   = (s) => /[0-9]/.test(s);
const hasSpecial = (s) => /[^A-Za-z0-9]/.test(s);

const COMMON = [
  'password','12345678','123456789','qwerty','abc123',
  '11111111','letmein','welcome'
];

// strength score (0–4)
const scorePassword = (password) => {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (hasLower(password) && hasUpper(password)) score++;
  if (hasDigit(password)) score++;
  if (hasSpecial(password)) score++;
  if (password.length >= 12 && score < 4) score++;
  return Math.min(score, 4);
};

const strengthLabel = (s) => ['Very weak','Weak','Fair','Good','Strong'][s];
const strengthClass = (s) => ['pw-veryweak','pw-weak','pw-fair','pw-good','pw-strong'][s];

// Server-aligned validation
const validatePassword = (password) => {
  if (password.length > MAX_PW_LEN) return 'Password cannot exceed 128 characters.';
  if (password.length < 8)         return 'Your password must contain at least 8 characters.';
  if (!hasLower(password))         return 'Password must include a lowercase letter.';
  if (!hasUpper(password))         return 'Password must include an uppercase letter.';
  if (!hasDigit(password))         return 'Password must include a number.';
  if (!hasSpecial(password))       return 'Password must include a special character.';
  if (COMMON.includes(password.toLowerCase())) {
    return "Your password can't be a commonly used password.";
  }
  return '';
};

// Normalize any server error (includes raw HTML 404s)
const normalizeServerError = (err) => {
  if (!err?.response) return 'Network error: please check your connection.';
  const { status, data } = err.response;

  const looksLikeHTML = typeof data === 'string' && /<\/?[a-z][\s\S]*>/i.test(data);
  if (looksLikeHTML) {
    if (status === 404) return 'Service temporarily unavailable. Please try again later.';
    if (status >= 500)  return 'Server error. Please try again shortly.';
    return 'Unexpected response from the server.';
  }

  if (typeof data === 'string') return data;
  if (data?.error) return String(data.error);
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return String(data.non_field_errors[0]);
  }

  if (data && typeof data === 'object') {
    const msgs = [];
    for (const v of Object.values(data)) {
      if (Array.isArray(v)) msgs.push(v.join(' '));
      else if (typeof v === 'string') msgs.push(v);
    }
    if (msgs.length) return msgs.join(' ');
  }
  return 'Something went wrong. Please try again.';
};

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    user_type: '',
  });

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [userTypeError, setUserTypeError] = useState('');
  const [resending, setResending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwScore, setPwScore] = useState(0);

  const disposableDomains = [
    'mailinator.com','10minutemail.com','guerrillamail.com','tempmail.com','yopmail.com'
  ];

  useEffect(() => {
    if (formData.confirm_password && formData.password !== formData.confirm_password) {
      setConfirmError('Passwords do not match.');
    } else {
      setConfirmError('');
    }
  }, [formData.password, formData.confirm_password]);

  const clearAllErrors = () => {
    setError(''); setEmailError(''); setPasswordError('');
    setConfirmError(''); setUserTypeError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const next = { ...formData, [name]: value };

    // If email cleared, reset pw fields + errors
    if (name === 'email' && value.trim() === '') {
      next.password = '';
      next.confirm_password = '';
      setFormData(next);
      setPwScore(0);
      clearAllErrors();
      return;
    }

    setFormData(next);

    if (name === 'email' || name === 'password' || name === 'confirm_password') {
      setError('');
    }

    if (name === 'email') {
      setEmailError(emailPattern.test(value) ? '' : 'Please enter a valid email address.');
    }
    if (name === 'password') {
      setPwScore(scorePassword(value));
      setPasswordError(validatePassword(value));
      setConfirmError(
        next.confirm_password && value !== next.confirm_password ? 'Passwords do not match.' : ''
      );
    }
    if (name === 'confirm_password') {
      setConfirmError(
        next.password && value !== next.password ? 'Passwords do not match.' : ''
      );
    }
  };

  const selectUserType = (type) => {
    setFormData((fd) => ({ ...fd, user_type: type }));
    setUserTypeError('');
  };

  const resendActivation = async (email) => {
    setResending(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/resend-activation/`, {
        email: email.toLowerCase(),
      });
      setMessage('A verification link has been sent to your email.');
      setError('');
    } catch {
      setError('Could not resend verification link. Please contact support.');
    }
    setResending(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');

    const emailDomain = (formData.email.split('@')[1] || '').toLowerCase();
    if (disposableDomains.includes(emailDomain)) {
      setError('Disposable or temporary email addresses are not allowed.');
      return;
    }
    if (!emailPattern.test(formData.email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!formData.user_type) {
      setUserTypeError('Please select Investor or Investee.');
      return;
    }
    if (formData.password !== formData.confirm_password) {
      setConfirmError('Passwords do not match.');
      return;
    }
    const pwdErr = validatePassword(formData.password);
    if (pwdErr) {
      setPasswordError(pwdErr);
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/register/`,
        {
          email: formData.email.toLowerCase(),
          password: formData.password,
          confirm_password: formData.confirm_password,
          user_type: formData.user_type.toLowerCase(),
          frontend_url: window.location.origin,
        }
      );
      setMessage(resp.data?.message || 'Account created. Please verify your email.A verification link has been sent to your email.');
      setTimeout(() => navigate('/login', { state: { email: formData.email } }), 4000);

    } catch (err) {
      const resp = err.response;

      if (resp?.status === 429) {
        const retrySec = parseInt(resp.headers?.['retry-after'] || '0', 10);
        const mins = Math.floor(retrySec / 60);
        const secs = retrySec % 60;
        const when =
          mins > 0
            ? `${mins} minute${mins > 1 ? 's' : ''}` +
              (secs > 0 ? ` and ${secs} second${secs > 1 ? 's' : ''}` : '')
            : `${secs} second${secs > 1 ? 's' : ''}`;
        setError(resp.data?.error || `Too many registration attempts. Try again in ${when}.`);
        return;
      }

      if (resp?.data?.inactive || resp?.data?.user_not_active) {
        setError(
          <>
            This account exists but is not yet activated.<br />
            <button
              disabled={resending}
              onClick={() => resendActivation(formData.email)}
              style={{ marginTop: 8, padding: '0.5em 1em', cursor: resending ? 'not-allowed' : 'pointer' }}
              type="button"
            >
              {resending ? 'Resending…' : 'Resend verification link'}
            </button>
          </>
        );
        return;
      }

      setError(normalizeServerError(err));
    } finally {
      setLoading(false);
    }
  };

  const isDisabled =
    !!emailError || !!passwordError || !!confirmError ||
    !formData.email || !formData.password || !formData.confirm_password || loading;

  const sLabel = strengthLabel(pwScore);
  const sClass = strengthClass(pwScore);
  const sWidth = `${(pwScore / 4) * 100}%`;

  // checklist booleans
  const pw = formData.password;
  const req = {
    min8: pw.length >= 8,
    max128: pw.length <= MAX_PW_LEN,
    lower: hasLower(pw),
    upper: hasUpper(pw),
    digit: hasDigit(pw),
    special: hasSpecial(pw),
    notCommon: pw ? !COMMON.includes(pw.toLowerCase()) : true,
  };

  return (
    <div className="auth-container">
      <h2>Join as Investor or Investee</h2>

      {/* User Type Selection */}
      <div className="user-type-selector">
        <div
          className={`type-card ${formData.user_type === 'investor' ? 'selected' : ''}`}
          onClick={() => selectUserType('investor')}
        >
          <h3>Investor</h3>
          <p>Looking to invest in promising ventures & also get funding as an investor</p>
        </div>
        <div
          className={`type-card ${formData.user_type === 'investee' ? 'selected' : ''}`}
          onClick={() => selectUserType('investee')}
        >
          <h3>Investee</h3>
          <p>Seeking funding and growth opportunities</p>
        </div>
      </div>
      {userTypeError && <span id="register-error">{userTypeError}</span>}

      {/* Registration Form */}
      <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
        {/* autofill decoys */}
        <input type="text" name="decoy-username" autoComplete="username" tabIndex={-1}
               style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
               aria-hidden="true" />
        <input type="password" name="decoy-password" autoComplete="current-password" tabIndex={-1}
               style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
               aria-hidden="true" />

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
          />
          {emailError && <span className="register-error-message">{emailError}</span>}
        </div>

        <div className="form-group">
          <label>Password (min. 8, max. 128)</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            maxLength={MAX_PW_LEN}
          />

          {/* Strength meter & label */}
          <div className="pw-wrap">
            <div className={`pw-meter ${pw ? 'show' : ''}`} aria-hidden="true">
              <div className={`pw-meter-fill ${sClass}`} style={{ width: sWidth }} />
            </div>
            <div className={`pw-strength-label ${sClass} ${pw ? 'show' : ''}`} aria-live="polite">
              {pw ? sLabel : ''}
            </div>
          </div>

          {/* Expanded checklist (includes too-long rule) */}
          <ul className="pw-req-list">
            <li className={req.min8 ? 'ok' : ''}>At least 8 characters</li>
            <li className={req.max128 ? 'ok' : 'err'}>No more than 128 characters</li>
            <li className={req.lower ? 'ok' : ''}>Lowercase letter</li>
            <li className={req.upper ? 'ok' : ''}>Uppercase letter</li>
            <li className={req.digit ? 'ok' : ''}>Number</li>
            <li className={req.special ? 'ok' : ''}>Special character</li>
            <li className={req.notCommon ? 'ok' : 'err'}>Not a common password</li>
          </ul>

          {passwordError && <span className="register-error-message">{passwordError}</span>}
        </div>

        <div className="form-group">
          <label>Confirm Password</label>
          <input
            type="password"
            name="confirm_password"
            value={formData.confirm_password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            maxLength={MAX_PW_LEN}
          />
          {confirmError && <span className="register-error-message">{confirmError}</span>}
        </div>

        <button type="submit" className="auth-button" disabled={isDisabled}>
          {loading ? 'Loading…' : 'Continue'}
        </button>

        {error && (
          <div className="register-error-inline">
            <span className="register-error-text">{error}</span>
            <button type="button" className="register-error-close" onClick={() => setError('')}>
              ×
            </button>
          </div>
        )}
      </form>

      <div className="auth-link">
        Already have an account? <Link to="/login">Login</Link>
      </div>
      {message && <div className="success">{message}</div>}
    </div>
  );
};

export default Register;
